// CSV round-trip for the admin directories.
// Every directory downloads as CSV and re-uploads in the same shape, so a
// tester can export, edit in a spreadsheet, and drop the file straight back.

import type { Bathrooms, Bedrooms, Community, GarageSize, Relationship, Resident, Unit, BoardSeat } from "./types";

export const COMMUNITY_COLUMNS = ["name", "street", "city", "state", "zip", "units"] as const;
export const UNIT_COLUMNS = ["address", "bedrooms", "bathrooms", "patio", "garage", "license_plates"] as const;
export const RESIDENT_COLUMNS = [
  "name", "email", "phone", "relationship", "board_member", "board_seat", "units", "signs_in_to",
] as const;

const cell = (v: unknown): string => {
  const s = v == null ? "" : String(v);
  return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
};

export const toCsv = (head: readonly string[], rows: unknown[][]): string =>
  [head as unknown[], ...rows].map((r) => r.map(cell).join(",")).join("\n");

/** RFC4180-ish parser: quoted cells, escaped quotes, CRLF tolerant. */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let buf = "";
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (quoted) {
      if (ch === '"' && text[i + 1] === '"') { buf += '"'; i++; }
      else if (ch === '"') quoted = false;
      else buf += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === ",") { row.push(buf); buf = ""; }
    else if (ch === "\n") { row.push(buf); rows.push(row); row = []; buf = ""; }
    else if (ch !== "\r") buf += ch;
  }
  if (buf.length || row.length) { row.push(buf); rows.push(row); }
  return rows.filter((r) => r.some((c) => c.trim() !== ""));
}

const yes = (v: string | undefined) => /^(y|yes|true|1)$/i.test((v || "").trim());

// ---------- export ----------

export const communityToCsv = (c: Partial<Community>): string =>
  toCsv(COMMUNITY_COLUMNS, [[c.name, c.address, c.city, c.state, c.zip, c.units]]);

export const unitsToCsv = (units: Unit[]): string =>
  toCsv(UNIT_COLUMNS, units.map((u) => [u.addr, u.beds, u.baths, u.patio ? "yes" : "no", u.garage, u.plates.join(" ")]));

export const residentsToCsv = (residents: Resident[], units: Unit[]): string =>
  toCsv(
    RESIDENT_COLUMNS,
    residents.map((r) => [
      r.name, r.email, r.phone, r.rel,
      r.board ? "yes" : "no", r.role,
      r.units.map((id) => units.find((u) => u.id === id)?.addr).filter(Boolean).join(" | "),
      r.board ? "board" : "resident",
    ])
  );

// ---------- import ----------

export function unitsFromCsv(text: string): Unit[] {
  return parseCsv(text)
    .slice(1)
    .map((r, i) => ({
      id: "u" + Date.now() + "-" + i,
      addr: (r[0] || "").trim(),
      beds: ((r[1] || "3").trim() as Bedrooms),
      baths: ((r[2] || "2").trim() as Bathrooms),
      patio: yes(r[3]),
      garage: ((r[4] || "None").trim() as GarageSize),
      plates: (r[5] || "").split(/[|,\s]+/).map((p) => p.trim().toUpperCase()).filter(Boolean),
    }))
    .filter((u) => u.addr.length > 0);
}

/** Resident `units` are pipe-separated street addresses resolved against `units`. */
export function residentsFromCsv(text: string, units: Unit[]): Resident[] {
  const byAddr = (a: string) => units.find((u) => u.addr.toLowerCase() === a.trim().toLowerCase())?.id;
  return parseCsv(text)
    .slice(1)
    .map((r, i) => {
      const board = yes(r[4]);
      return {
        id: "p" + Date.now() + "-" + i,
        name: (r[0] || "").trim(),
        email: (r[1] || "").trim(),
        phone: (r[2] || "").trim(),
        rel: (/tenant/i.test(r[3] || "") ? "Tenant" : "Owner") as Relationship,
        board,
        role: board ? (((r[5] || "").trim() || "Member at large") as BoardSeat) : "",
        units: (r[6] || "").split("|").map(byAddr).filter((v): v is string => Boolean(v)),
      };
    })
    .filter((p) => p.name.length > 0);
}

export function communityFromCsv(text: string): Partial<Community> {
  const [, r = []] = parseCsv(text);
  return {
    name: r[0] || "", address: r[1] || "", city: r[2] || "",
    state: (r[3] || "").toUpperCase(), zip: r[4] || "",
    units: parseInt(r[5] || "0", 10) || 0,
  };
}

// ---------- browser helpers ----------

export function downloadCsv(filename: string, text: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export function readCsvFile(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result));
    fr.onerror = () => reject(fr.error);
    fr.readAsText(file);
  });
}
